import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Button } from "@akuna/ui";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { AddressLink } from "@/components/shared/AddressLink";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { isPetDeceased } from "@/lib/utils/pet-death";
import { formatPetAge } from "@/lib/date-utils";

interface OwnerPhone {
  id?: string | null;
  phone_number?: string | null;
  is_primary?: boolean | null;
  is_whatsapp?: boolean | null;
}

interface OwnerEmail {
  id?: string | null;
  email?: string | null;
  is_primary?: boolean | null;
}

interface OwnerAddress {
  id?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  zip_code?: string | null;
  is_primary?: boolean | null;
  city?: { name?: string | null; state?: { name?: string | null } | null } | null;
}

interface OwnerPet {
  id: string;
  name: string;
  birth_date?: string | null;
  status?: { name?: string | null } | null;
  species?: { name?: string | null } | null;
  breed?: { name?: string | null } | null;
  photo?: { file_path?: string | null } | Array<{ file_path?: string | null }> | null;
}

interface MobileOwnerOverviewTabProps {
  ownerId: string;
  ownerName: string;
  phones: OwnerPhone[];
  primaryEmail: OwnerEmail | null;
  addresses: OwnerAddress[];
  documentTypeName: string | null;
  formattedDocument: string | null;
  birthDate: string | null;
  profession: string | null;
  leadSourceName: string | null;
  marketingConsent: boolean;
  pets: OwnerPet[] | undefined;
  isLoadingPets: boolean;
  petsError: unknown;
  editButton?: React.ReactNode;
}

const CARD = "bg-card border border-border/60 rounded-[20px] p-[18px] shadow-soft-card";
const LABEL = "text-[11px] uppercase tracking-[0.06em] text-muted-foreground/70 font-semibold mb-[3px]";
const VALUE = "text-[14px] font-semibold text-foreground";

function resolvePhotoPath(photo: OwnerPet["photo"]): string | null {
  if (!photo) return null;
  if (Array.isArray(photo)) return photo[0]?.file_path ?? null;
  return photo.file_path ?? null;
}

export function MobileOwnerOverviewTab({
  ownerId,
  ownerName,
  phones,
  primaryEmail,
  addresses,
  documentTypeName,
  formattedDocument,
  birthDate,
  profession,
  leadSourceName,
  marketingConsent,
  pets,
  isLoadingPets,
  petsError,
  editButton,
}: MobileOwnerOverviewTabProps) {
  const { t } = useTranslation("patients");

  return (
    <div className="flex flex-col gap-3.5">

      {/* Identity card */}
      <div className={`${CARD} flex items-center gap-3.5`}>
        <div className="flex-none w-[60px] h-[60px] rounded-full bg-secondary flex items-center justify-center">
          <User className="h-[28px] w-[28px] text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <span
            className="font-extrabold text-[20px] tracking-tight text-foreground block truncate"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {ownerName}
          </span>
          {phones.length > 0 && (
            <span className="text-[13px] text-muted-foreground truncate block">
              {phones[0].phone_number}
            </span>
          )}
        </div>
        {editButton ? <div className="flex-none self-start">{editButton}</div> : null}
      </div>

      {/* Contact card */}
      {(phones.length > 0 || primaryEmail?.email) && (
        <div className={CARD}>
          <p className={LABEL}>{t("ownerProfile.fields.contact")}</p>
          <div className="flex flex-col gap-2 mt-2">
            {phones.map((phone, index) => (
              <div key={phone.id ?? `${phone.phone_number}-${index}`} className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground flex-none" />
                {phone.is_whatsapp ? (
                  <WhatsAppPhoneLink
                    phoneNumber={phone.phone_number}
                    className="min-w-0"
                    textClassName={`truncate ${VALUE}`}
                  />
                ) : (
                  <span className={VALUE}>{phone.phone_number}</span>
                )}
              </div>
            ))}
            {primaryEmail?.email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground flex-none" />
                <span className={`${VALUE} truncate`}>{primaryEmail.email}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data grid card */}
      {(formattedDocument || birthDate || profession || leadSourceName) && (
        <div className={CARD}>
          <p className={LABEL}>{t("ownerProfile.mobileDataTitle")}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-3">
            {formattedDocument && (
              <div>
                <p className={LABEL}>{t("ownerProfile.fields.document")}</p>
                <p className={VALUE}>{documentTypeName ? `${documentTypeName}: ${formattedDocument}` : formattedDocument}</p>
              </div>
            )}
            {birthDate && (
              <div>
                <p className={LABEL}>{t("ownerProfile.fields.birthDate")}</p>
                <p className={VALUE}>{birthDate}</p>
              </div>
            )}
            {profession && (
              <div>
                <p className={LABEL}>{t("ownerProfile.fields.profession")}</p>
                <p className={VALUE}>{profession}</p>
              </div>
            )}
            {leadSourceName && (
              <div>
                <p className={LABEL}>{t("ownerProfile.fields.leadSource")}</p>
                <p className={VALUE}>{leadSourceName}</p>
              </div>
            )}
            <div>
              <p className={LABEL}>{t("ownerProfile.fields.marketing")}</p>
              <p className={VALUE}>{marketingConsent ? t("ownerProfile.fields.authorized") : t("ownerProfile.fields.notAuthorized")}</p>
            </div>
          </div>
        </div>
      )}

      {/* Addresses card */}
      {addresses.length > 0 && (
        <div className={CARD}>
          <p className={LABEL}>{t("ownerProfile.fields.addresses")}</p>
          <div className="flex flex-col gap-2 mt-2">
            {addresses.map((address) => {
              const parts = [
                address.street,
                address.number,
                address.complement,
                address.neighborhood,
                address.city?.name,
                address.city?.state?.name,
                address.zip_code,
              ].filter(Boolean);
              const addressText = parts.join(", ");
              return (
                <div key={address.id ?? `${address.street}-${address.number}`} className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-none" />
                  <AddressLink addressText={addressText}>
                    <span className="text-[14px] font-semibold text-[13px]">
                      {parts.length ? addressText : t("ownerProfile.fields.addressNotProvided")}
                    </span>
                  </AddressLink>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pets card */}
      <div className={CARD}>
        <div className="flex items-center justify-between mb-3">
          <p className={LABEL}>{t("ownerProfile.petsSection.title")}</p>
          <Button variant="outline" size="sm" asChild className="h-7 text-xs px-2">
            <Link to={`/patients/new?owner_id=${encodeURIComponent(ownerId)}`}>
              {t("ownerProfile.petsSection.addPet")}
            </Link>
          </Button>
        </div>

        {petsError ? (
          <p className="text-sm text-muted-foreground">{t("ownerProfile.petsSection.loadError")}</p>
        ) : isLoadingPets ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </div>
        ) : pets && pets.length > 0 ? (
          <div className="flex flex-col gap-2">
            {pets.map((pet) => {
              const photoPath = resolvePhotoPath(pet.photo);
              const ageLabel = pet.birth_date ? formatPetAge(pet.birth_date) : null;
              return (
                <Link
                  key={pet.id}
                  to={`/patients/pets/${pet.id}`}
                  className="group flex items-center gap-3 rounded-xl border border-border/60 bg-background/60 p-3 hover:border-primary/50 hover:bg-secondary transition-all"
                >
                  <Avatar className="h-10 w-10 flex-none">
                    <AvatarImage src={photoPath ?? undefined} alt={pet.name} />
                    <AvatarFallback className="bg-muted/50 text-muted-foreground">
                      <CustomIcons name="paws" className="h-4 w-4 text-current" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[14px] text-foreground truncate">{pet.name}</p>
                      {pet.status?.name && isPetDeceased(pet) && (
                        <Badge variant="outline" className="text-[11px] px-1.5 py-0">
                          {pet.status.name}
                        </Badge>
                      )}
                    </div>
                    <p className="text-[12px] text-muted-foreground truncate">
                      {[pet.species?.name, pet.breed?.name, ageLabel].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <span className="text-muted-foreground text-[14px] group-hover:translate-x-0.5 transition-transform">→</span>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <CustomIcons name="paws" className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t("ownerProfile.petsSection.emptyDescription")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
